import React, { useEffect, useMemo, useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getUrlFromPipeline, DEAL_STATUS_TYPES } from '@/utils/constants';
import { paths } from '@/app/routes';
import ArticleIcon from '@mui/icons-material/Article';
import { useNavigate, useLocation } from 'react-router-dom';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import {
	useDeleteDealMutation,
	useGetDealPoliciesQuery,
	useGetDealRowQuery,
	useRemoveDealPolicyMutation,
	useUpdateDealRowMutation,
} from '@/features/deals/dealsApi';
import { showToast } from '@/features/toast/toastSlice';
import { useDispatch, useSelector } from 'react-redux';
import { IS_DEV } from '@/utils/environment';
import { useObjectContext } from '@/hooks/useObjectContext';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { usePipelineContext } from '@/context/pipelineContext';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { triggerRefresh } from '@/features/table/dealsTableSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import AddIcon from '@mui/icons-material/Add';
import { openDialog } from '@/features/dialog/dialogSlice';

export const GetMenuLink = ({
	deal: dealProp,
	hideViewDeal = false,
	policy,
	slotProps = { button: {} },
	...props
}) => {
	const { button } = slotProps ?? {};
	const { sx: buttonSx, ...buttonProps } = button ?? {};

	const theme = useTheme();

	const { clientRow, dealId: contextDealId, dealRow: contextDeal } = useObjectContext();
	const dealId = dealProp?.dealId ?? contextDealId;
	const deal = dealProp ?? contextDeal;

	const [anchorEl, setAnchorEl] = useState(null);

	const open = Boolean(anchorEl);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const isDebtor = deal?.objectType === 'debtor';

	const policiesQuery = useGetDealPoliciesQuery({ dealId }, { skip: !dealId });

	const policies = policiesQuery?.data || [];

	const location = useLocation();
	const isClientView = location.pathname.includes(paths.clients);

	const pipeline = deal?.pipeline;

	const [showDialog, setShowDialog] = useState(false);
	const objectType =
		pipeline === process.env.REACT_APP_PIPELINE_CLAIMS ||
		(IS_DEV && pipeline === process.env.REACT_APP_PIPELINE_ENDORSEMENTS)
			? 'ticket'
			: (deal?.objectType ?? 'deal');

	const [deleteDeal, { isLoadingDeal }] = useDeleteDealMutation({
		fixedCacheKey: `delete-deal-${dealId}-${objectType}`,
	});

	const isLoading = deal?.policiesLoading || isLoadingDeal;

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const { checkEndorsementStage, stages, inactive } = usePipelineContext(pipeline);

	const { data: dealRow } = useGetDealRowQuery(dealId, {
		skip: !dealId,
	});

	const isDealArchived = useMemo(() => {
		return dealRow?.status === DEAL_STATUS_TYPES.archived;
	}, [dealRow]);

	const isRenewal = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_RENEWALS;
	const isEndorsement = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_ENDORSEMENTS;

	const hasCurrentPolicyTransactions = useMemo(() => {
		const policiesToCheck =
			Array.isArray(deal?.policies) && deal.policies.length > 0
				? deal.policies
				: Array.isArray(policies)
					? policies
					: [];

		return policiesToCheck.some((policy) => {
			const transaction = policy?.transaction ?? {};
			return transaction?.isCurrent !== false;
		});
	}, [deal?.policies, policies]);

	const isEndorsementStage = checkEndorsementStage(dealRow?.dealStage);

	const dealLink = useMemo(() => {
		const path = getUrlFromPipeline(pipeline);
		return `${path}/${dealId}`;
	}, [deal, pipeline]);

	const [removeDealPolicy] = useRemoveDealPolicyMutation();
	const handleRemoveDealPolicy = () => {
		if (policy) {
			removeDealPolicy({
				dealId,
				clientId: clientRow?.clientId,
				policyId: isDebtor ? policy?.PolicyId : policy?.Id,
				...(isDebtor && { policyTransactionId: policy?.Id }),
			})
				.unwrap()
				.then((response) => {
					dispatch(
						showToast({
							message: `${isDebtor ? 'Invoice' : 'Policy'} ${policy?.PolicyNumber} - ${policy?.Description ?? policy?.BriefDescription} unlinked`,
							action: {
								label: `Unlink ${isDebtor ? 'invoice' : 'policy'}`,
							},
							autohide: false,
							duration: 5000,
						}),
					);
				})
				.catch((error) => {
					dispatch(
						showToast({
							message: `Error unlinking ${isDebtor ? 'invoice' : 'policy'} ${policy?.PolicyNumber} - ${policy?.Description ?? policy?.BriefDescription}`,
							action: {
								label: `Unlink ${isDebtor ? 'invoice' : 'policy'}`,
							},
							autohide: false,
							type: 'error',
							duration: 5000,
						}),
					);
					console.error('Error unlinking policy:', error);
				});
		} else {
			console.error('missing policy: ', policy);
		}
	};

	const [updateDeal] = useUpdateDealRowMutation();

	const handleDealStatusUpdate = async () => {
		if (deal && dealRow) {
			console.log('clicked: ', isDealArchived);
			await updateDeal({
				dealId,
				docId: dealRow?.id,
				properties: {
					status: isDealArchived ? null : DEAL_STATUS_TYPES.archived,
				},
			}).unwrap();
			setTimeout(async () => {
				dispatch(triggerRefresh());
			}, 2000);
		}
		handleClose();
	};

	return (
		<>
			<IconButton
				aria-label='edit'
				onClick={handleClick}
				size={'small'}
				sx={{
					...(buttonSx ?? {}),
					'&:hover': {
						...(buttonSx?.['&:hover'] ?? {}),
					},
				}}
			>
				<MoreVertIcon />
			</IconButton>
			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				{!hideViewDeal && (
					<MenuItem
						onClick={(e) => {
							handleClose();
							navigate(dealLink);
						}}
					>
						<ListItemIcon>
							<ArticleIcon sx={{ color: theme.palette.primary.main }} />
						</ListItemIcon>
						<ListItemText sx={{ color: theme.palette.primary.main }}>View deal</ListItemText>
					</MenuItem>
				)}
				{(deal?.policies?.length > 0 || policies?.length > 0) && !isClientView && (
					<MenuItem
						onClick={() => {
							dispatch(
								openDialog({
									dialogName: 'editPolicyTransaction',
									data: { type: 'transaction', policy: policy },
									// data: { type: isDebtor ? 'transaction' : 'policy' },
								}),
							);
						}}
					>
						<ListItemIcon>
							<AddIcon
								sx={{
									backgroundColor: theme.palette.primary.main,
									width: '22px',
									height: '22px',
									borderRadius: 2,
									color: 'white',
								}}
							/>
						</ListItemIcon>
						<ListItemText sx={{ color: theme.palette.primary.main }}>
							{'Add policy transaction'}
						</ListItemText>
					</MenuItem>
				)}
				{!isClientView &&
					(inactive.includes(deal?.dealStage) ||
						isRenewal ||
						isEndorsementStage ||
						isDebtor) && (
						<MenuItem
							onClick={() => {
								handleClose();
								setShowDialog(true);
							}}
						>
							<ListItemIcon>
								<LinkOffIcon
									sx={{
										backgroundColor: theme.palette.primary.main,
										width: '22px',
										height: '22px',
										borderRadius: 2,
										color: 'white',
									}}
								/>
							</ListItemIcon>
							<ListItemText
								sx={{ color: theme.palette.primary.main }}
							>{`Unlink ${isDebtor ? 'invoice' : 'policy'}`}</ListItemText>
						</MenuItem>
					)}

				{!hideViewDeal && (
					<MenuItem disabled={isLoading} onClick={handleDealStatusUpdate}>
						<ListItemIcon>
							{isDealArchived ? (
								<UnarchiveIcon sx={{ color: theme.palette.primary.main }} />
							) : (
								<ArchiveIcon sx={{ color: theme.palette.primary.main }} />
							)}
						</ListItemIcon>
						<ListItemText sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
							{isDealArchived ? 'Unarchive deal' : 'Archive deal'}
						</ListItemText>
					</MenuItem>
				)}
			</Menu>
			{showDialog && (
				<ConfirmDialog
					openDialog={showDialog}
					handleClose={() => setShowDialog(false)}
					handleConfirm={handleRemoveDealPolicy}
					actionName={`unlink ${isDebtor ? 'invoice' : 'policy'}`}
					title={false}
					canBeUndone={false}
					additionalContent={`${policy?.PolicyNumber} - ${policy?.Description ?? policy?.BriefDescription} will be unlinked from ${deal?.dealName}.`}
				/>
			)}
		</>
	);
};
